/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import Downshift, { Actions, PropGetters } from 'downshift';
import {
  Popper,
  Paper,
  MenuList,
  MenuItem,
  Typography,
} from '@material-ui/core';
import styles from './styles';
interface InjectedProps<Item> {
  getInputProps: PropGetters<Item>['getInputProps'];
  getLabelProps: PropGetters<Item>['getLabelProps'];
  getToggleButtonProps: PropGetters<Item>['getToggleButtonProps'];
  openMenu: Actions<Item>['openMenu'];
  closeMenu: Actions<Item>['closeMenu'];
  isOpen: boolean;
  inputRef: (input: HTMLInputElement) => void;
  getRootProps: PropGetters<Item>['getRootProps'];
}

interface PropTypes<Item> {
  id?: string;
  suggestions: Item[];
  itemToString: (item: Item | null) => string;
  onSelect?: (item: Item) => unknown;
  onInputValueChange?: (value: string) => void;
  initialSelectedItem?: Item;
  children: (props: InjectedProps<Item>) => React.ReactElement;
  enabled?: boolean;
  inputValue?: string | null;
  overflow?: boolean;
  noSuggestions?: { message: string };
  shouldBoldSelectedItem?: boolean;
}

function SuggestionItem<Item>({
  getItemProps,
  item,
  itemToString,
  highlight,
  bold,
}: {
  getItemProps: PropGetters<Item>['getItemProps'];
  item: Item;
  itemToString: PropTypes<Item>['itemToString'];
  highlight: boolean;
  bold: boolean;
}): React.ReactElement {
  const props = getItemProps({
    item,
    style: {
      backgroundColor: highlight ? 'lightgray' : 'white',
      fontWeight: bold ? 'bold' : 'normal',
    },
  });
  return (
    <MenuItem {...props}>
      <Typography variant="inherit" noWrap>
        {itemToString(item)}
      </Typography>
    </MenuItem>
  );
}

function Autocomplete<ItemType>({
  id,
  suggestions,
  itemToString,
  onSelect,
  children,
  inputValue,
  onInputValueChange,
  initialSelectedItem,
  enabled = true,
  overflow = false,
  noSuggestions,
  shouldBoldSelectedItem = false,
}: PropTypes<ItemType>): React.ReactElement {
  const [childNode, setChildNode] = useState<HTMLInputElement>();

  return (
    <Downshift
      id={id}
      itemToString={itemToString}
      onSelect={onSelect}
      inputValue={inputValue || ''}
      onInputValueChange={onInputValueChange}
      initialSelectedItem={initialSelectedItem}
    >
      {downshift => {
        const {
          getInputProps,
          getItemProps,
          getLabelProps,
          getMenuProps,
          getToggleButtonProps,
          getRootProps,
          openMenu,
          closeMenu,
          isOpen,
          highlightedIndex,
        } = downshift;

        const menuProps = isOpen
          ? getMenuProps({}, { suppressRefError: true })
          : {};

        const child = children({
          getInputProps,
          getLabelProps,
          getToggleButtonProps,
          openMenu,
          closeMenu,
          isOpen,
          inputRef: setChildNode,
          getRootProps,
        });

        const menuWidth = childNode ? childNode.clientWidth + 2 : 0;

        return (
          <div>
            {child}
            {childNode && (
              <Popper
                open={enabled && isOpen}
                anchorEl={childNode}
                css={styles.popper}
                placement="bottom"
              >
                <Paper css={{ width: menuWidth }}>
                  <MenuList
                    {...menuProps}
                    css={overflow ? styles.popperOverflow : null}
                  >
                    {suggestions.length === 0 && noSuggestions ? (
                      <MenuItem disabled>
                        <Typography variant="inherit" noWrap>
                          {noSuggestions.message}
                        </Typography>
                      </MenuItem>
                    ) : (
                      suggestions.map((item, i) => {
                        return (
                          <SuggestionItem
                            key={i}
                            getItemProps={getItemProps}
                            item={item}
                            itemToString={itemToString}
                            highlight={highlightedIndex === i}
                            bold={
                              shouldBoldSelectedItem &&
                              itemToString(initialSelectedItem || null) ===
                                itemToString(item)
                            }
                          />
                        );
                      })
                    )}
                  </MenuList>
                </Paper>
              </Popper>
            )}
          </div>
        );
      }}
    </Downshift>
  );
}

export default Autocomplete;
